import { keyBy } from "es-toolkit";
import { sharedQueryClient } from "~/lib/query-client";
import { fetchBadgeDefinitions, fetchProductsForBadges } from "~/lib/badges/fetchers";
import { evaluateBadge } from "~/lib/badges/rules";
import type { BadgeResult } from "~/lib/badges/types";

const BATCH_SIZE = 200;
const PRODUCT_TTL_MS = 5 * 60 * 1000;
const DEFINITION_STALE_MS = 15 * 60 * 1000;

export class BadgeStore {
  results = $state<Record<string, BadgeResult | null | undefined>>({});

  #pending: string[] = [];
  #batchTimer: ReturnType<typeof setTimeout> | null = null;
  #timestamps: Record<string, number> = {};
  #inflight: Record<string, true> = {};

  seed(productId: string, result: BadgeResult | null): void {
    this.results[productId] = result;
    this.#timestamps[productId] = Date.now();
  }

  register(productId: string): void {
    const isFresh =
      productId in this.results &&
      this.#timestamps[productId] !== undefined &&
      Date.now() - this.#timestamps[productId] < PRODUCT_TTL_MS;

    if (isFresh || productId in this.#inflight) return;

    if (!this.#pending.includes(productId)) {
      this.#pending.push(productId);
    }

    if (!this.#batchTimer) {
      this.#batchTimer = setTimeout(() => void this.#processBatch(), 0);
    }
  }

  async #processBatch(): Promise<void> {
    this.#batchTimer = null;
    const ids = this.#pending.splice(0);
    if (!ids.length) return;

    ids.forEach((id) => {
      this.#inflight[id] = true;
    });

    try {
      const definitions = await sharedQueryClient.fetchQuery({
        queryKey: ["badge-definitions"],
        queryFn: fetchBadgeDefinitions,
        staleTime: DEFINITION_STALE_MS,
      });

      if (!definitions.length) {
        const now = Date.now();
        ids.forEach((id) => {
          this.results[id] = null;
          this.#timestamps[id] = now;
          delete this.#inflight[id];
        });
        return;
      }

      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        const batch = ids.slice(i, i + BATCH_SIZE);
        const products = await fetchProductsForBadges(batch);
        const productMap = keyBy(products, (p) => p.id);
        const now = Date.now();

        batch.forEach((productId) => {
          const product = productMap[productId];
          this.results[productId] = product ? evaluateBadge(product, definitions) : null;
          this.#timestamps[productId] = now;
          delete this.#inflight[productId];
        });
      }
    } catch (error: unknown) {
      console.warn("[Aurora] BadgeStore failed to load badge data:", error);
      ids.forEach((id) => {
        delete this.#inflight[id];
        if (!(id in this.results)) {
          this.results[id] = null;
          // No timestamp written on error so failed products can be retried immediately.
        }
      });
    }
  }
}

export const badgeStore = new BadgeStore();
